import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { ArrowLeft, Download, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { makeApi, authedUrl } from "@/lib/api";
import { money, dateShort } from "@/lib/format";
import { useAuthedBlob } from "@/lib/useAuthedBlob";
import { InvoiceStatusPill, DraftOptions, StatusActions, MissingSettingsNote } from "@/components/invoices/InvoiceControls";

const Meta = ({ label, children, testId }) => (
  <div className="flex justify-between gap-6 text-sm"><span className="text-slate-500">{label}</span><span className="font-medium text-right" data-testid={testId}>{children}</span></div>
);

const day = (d) => (d ? dateShort(`${d}T12:00:00`) : "");

const CarrierHeader = ({ inv, logo }) => {
  const c = inv.company || {};
  const ids = [c.mc_number && `MC ${c.mc_number}`, c.dot_number && `DOT ${c.dot_number}`].filter(Boolean).join(" · ");
  return (
    <div className="flex items-start gap-4 min-w-0" data-testid="invoice-from">
      {logo && <img src={logo} alt="" className="max-h-20 max-w-[160px] object-contain" data-testid="invoice-logo" />}
      <div className="min-w-0">
        <div className="text-xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="invoice-company-name">{c.name || "Your Company"}</div>
        {c.address && <div className="text-sm text-slate-600 whitespace-pre-line">{c.address}</div>}
        {(c.phone || c.email) && <div className="text-sm text-slate-600">{[c.phone, c.email].filter(Boolean).join(" · ")}</div>}
        {ids && <div className="text-xs text-slate-500 mt-0.5" data-testid="invoice-company-ids">{ids}</div>}
      </div>
    </div>
  );
};

export default function InvoiceView() {
  const { id } = useParams();
  const { getToken } = useAuth();
  const [inv, setInv] = useState(null);
  const api = makeApi(getToken);
  const logo = useAuthedBlob(inv?.has_logo ? `/invoices/${id}/logo` : null);

  const refresh = async () => { const { data } = await api.get(`/invoices/${id}`); setInv(data); };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (!inv) return <div className="text-slate-500">Loading…</div>;
  const load = inv.load || {};

  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/invoices/${id}`, body);
      setInv(data);
      toast.success(body.status ? `Invoice marked ${body.status === "Sent" && inv.status === "Paid" ? "unpaid" : body.status}` : "Invoice updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update invoice");
      throw e;
    }
  };
  const open = async (path) => window.open(await authedUrl(path, getToken), "_blank");

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="invoice-view-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to={`/admin/loads/${inv.load_id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="w-4 h-4" /> Back to load</Link>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => open(`/invoices/${id}/pdf?inline=1`)} data-testid="print-invoice-btn">Print preview</Button>
          <Button variant="outline" onClick={() => open(`/invoices/${id}/pdf?inline=0`)} data-testid="download-invoice-btn"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
          <Button variant="outline" onClick={() => open(`/loads/${inv.load_id}/packet`)} data-testid="download-packet-btn"><Package className="w-4 h-4 mr-1" /> Export packet</Button>
          <StatusActions inv={inv} onPatch={patch} />
        </div>
      </div>

      <MissingSettingsNote inv={inv} />
      {inv.status === "Draft" && <DraftOptions inv={inv} onPatch={patch} />}

      <Card className="p-6 md:p-10 border-slate-200 bg-white">
        <div className="flex items-start justify-between flex-wrap gap-6 pb-6 border-b border-slate-200">
          <CarrierHeader inv={inv} logo={logo} />
          <div className="min-w-[220px] space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <div className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>INVOICE</div>
              <InvoiceStatusPill inv={inv} testId="invoice-status" />
            </div>
            <Meta label="Invoice #" testId="invoice-number">{inv.invoice_number}</Meta>
            <Meta label="Invoice date">{dateShort(inv.date)}</Meta>
            <Meta label="Terms" testId="invoice-terms">{inv.terms_label}</Meta>
            <Meta label="Due date" testId="invoice-due">{inv.due_date ? day(inv.due_date) : "When sent"}</Meta>
            {inv.days_outstanding != null && <Meta label={inv.status === "Paid" ? "Days to pay" : "Days outstanding"} testId="invoice-aging">{inv.days_outstanding}</Meta>}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 py-6">
          <div data-testid="invoice-bill-to"><div className="text-xs text-slate-500 uppercase tracking-wider">Bill to</div>
            <div className="mt-1 font-medium">{inv.bill_to?.name || inv.broker_name}</div>
            {inv.bill_to?.address && <div className="text-sm text-slate-600 whitespace-pre-line">{inv.bill_to.address}</div>}
            {inv.bill_to?.contact_name && <div className="text-sm text-slate-600">Attn: {inv.bill_to.contact_name}</div>}
            {(inv.bill_to?.phone || inv.bill_to?.email) && <div className="text-sm text-slate-600">{[inv.bill_to.phone, inv.bill_to.email].filter(Boolean).join(" · ")}</div>}
            {inv.bill_to?.mc_number && <div className="text-xs text-slate-500">MC {inv.bill_to.mc_number}</div>}
            {inv.status === "Draft" && !inv.bill_to?.address && <Link to="/admin/brokers" className="text-xs text-amber-800 underline" data-testid="invoice-bill-to-missing">Add this broker&apos;s billing address</Link>}
          </div>
          <div><div className="text-xs text-slate-500 uppercase tracking-wider">Load #</div><div className="mt-1 font-medium" data-testid="invoice-carrier-number">{load.carrier_number || load.load_number}</div>{load.carrier_number && <div className="text-xs text-slate-500" data-testid="invoice-broker-ref">Broker ref #{load.load_number}</div>}</div>
          <div><div className="text-xs text-slate-500 uppercase tracking-wider">Route</div><div className="mt-1 text-sm">{load.pickup_location} → {load.delivery_location}</div></div>
        </div>

        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider"><th className="text-left px-4 py-3">Description</th><th className="text-right px-4 py-3">Amount</th></tr></thead>
          <tbody>
            {(inv.line_items || []).map((ln, i) => (
              <tr key={i} data-testid={`invoice-line-${i}`}>
                <td className="px-4 py-4 border-t border-slate-200"><div className="font-medium">{ln.description}</div>{ln.detail && <div className="text-xs text-slate-500 mt-0.5">{ln.detail}</div>}</td>
                <td className="px-4 py-4 border-t border-slate-200 text-right font-medium">{money(ln.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex flex-col-reverse md:flex-row md:items-start justify-between gap-6">
          <div className="md:max-w-sm" data-testid="invoice-remit">
            <div className="text-xs text-slate-500 uppercase tracking-wider">Remit payment to {inv.remit_profile === "factored" ? "(factoring)" : ""}</div>
            <div className="mt-1 text-sm whitespace-pre-line">{inv.remit_text || <span className="text-slate-400">Not set. Add it in Settings.</span>}</div>
          </div>
          <div className="w-full md:w-72">
            <div className="flex items-center justify-between py-3 border-t border-slate-200">
              <span className="text-sm font-semibold">Total due</span>
              <span className="text-2xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="invoice-total">{money(inv.amount)}</span>
            </div>
            <div className="text-xs text-slate-500 text-right">{inv.terms_label}{inv.due_date ? ` · due ${day(inv.due_date)}` : ""}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
